// Sprint 6-organs §6.2 — деталь органа (route /markers/organ/:code).
// items = карточка биомаркера (sortByTier, optimum_status) + сворачиваемый блок
// «ещё не загружено» (missing, D3) → deep-link на загрузку. Клик по маркеру →
// деталь биомаркера (setMarkerOrigin, как из MarkersTab — history.back вернёт сюда).
//
// Копирайт §0: «Показатели, связанные с … органом», фактический счёт статусов,
// НЕ «состояние/диагноз органа», НЕ балл.

import { useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronDown, ChevronUp, UploadCloud } from 'lucide-react';
import { useOrgan } from '@/api/hooks/organs';
import { peekMarkerOrigin, setMarkerOrigin } from '@/lib/markerNavState';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { SafeBiomarkerCard } from '@/components/dashboard/BiomarkerCard';
import { hasHistory } from '@/components/dashboard/HistoryHint';
import { CatalogSkeleton } from '@/components/Skeletons';
import { sortByTier } from '@/lib/biomarkerStatus';
import { ORGAN_RELATED_CAPTION, statusCountsSummary } from '@/lib/organView';
import { organLabelFor } from '@/lib/organ-taxonomy';
import { capitalizeFirst } from '@/lib/utils';

export function OrganDetail({ code }: { code: string }) {
  const { data, isPending, isError } = useOrgan(code);
  const [, setLocation] = useLocation();
  const [missingOpen, setMissingOpen] = useState(false);

  // «Назад» — на вкладку «Маркеры» (ScrollToTop восстановит её scroll: prev
  // '/markers/organ/...' startsWith '/markers/'). Тайл-открытие в MarkersTab уже
  // сохранило состояние таба (navigateAway).
  const handleBack = () => {
    if (peekMarkerOrigin()) window.history.back();
    else setLocation('/markers');
  };

  // Origin ставим при уходе в карточку маркера — чтобы history.back() из детали
  // вернулся ровно на эту органную страницу.
  const openMarker = (slug: string) => {
    setMarkerOrigin(`/markers/organ/${code}`);
    setLocation(`/markers/${slug}`);
  };

  if (isPending) {
    return (
      <MobileFrame>
        <ScreenHeader title="Орган" onBack={handleBack} />
        <CatalogSkeleton />
      </MobileFrame>
    );
  }
  if (isError || !data) {
    return (
      <MobileFrame>
        <ScreenHeader title="Орган" onBack={handleBack} />
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          Не удалось загрузить показатели органа.
        </div>
      </MobileFrame>
    );
  }

  const label = data.organ_label_ru || organLabelFor(data.organ_code);
  const items = sortByTier(data.items);
  const summary = statusCountsSummary({
    out_of_range: items.filter((i) => i.clinical_status === 'out_of_range').length,
    attention: items.filter(
      (i) => i.clinical_status === 'in_range' && i.optimum_status === 'suboptimal',
    ).length,
    in_range: items.filter(
      (i) => i.clinical_status === 'in_range' && i.optimum_status !== 'suboptimal',
    ).length,
  });

  // Покрытие органа: сколько показателей всего / у юзера / докупить (comment 2+3).
  const haveCount = items.length;
  const totalMarkers = haveCount + data.missing.length;
  const coveragePct = totalMarkers > 0 ? Math.round((haveCount / totalMarkers) * 100) : 0;

  return (
    <MobileFrame>
      <ScreenHeader title={label} onBack={handleBack} />

      <main className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 pb-6 pt-1">
        <div>
          <p className="text-hl-small text-muted-foreground">{ORGAN_RELATED_CAPTION}</p>
          {summary && <p className="mt-0.5 text-hl-secondary text-gray-700">{summary}</p>}
        </div>

        {items.length === 0 && (
          <div className="rounded-surface border border-border bg-surface p-4 text-center text-sm text-muted-foreground">
            Пока нет данных по этому органу.
          </div>
        )}

        {items.map((item) => (
          <SafeBiomarkerCard
            key={item.biomarker_code}
            name={item.canonical_name_ru}
            normalRangeLabel={item.normal_range_label}
            value={item.value_normalized}
            unit={item.unit_normalized}
            valueText={item.value_qualitative}
            clinicalStatus={item.clinical_status}
            optimumStatus={item.optimum_status}
            delta={item.delta}
            hasHistory={hasHistory(item.points_count)}
            onClick={() => openMarker(item.biomarker_code)}
          />
        ))}

        {data.missing.length > 0 && (
          // shrink-0: иначе блок сжимается в flex-колонке и overflow режет кнопку.
          <div className="shrink-0 rounded-surface border border-dashed border-gray-300 bg-muted p-4">
            <p className="text-sm font-semibold text-foreground">
              Полная картина: {totalMarkers} показателей
            </p>
            <p className="mt-0.5 text-hl-secondary text-muted-foreground">
              У вас {haveCount} · ещё {data.missing.length} можно добавить
            </p>

            <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${coveragePct}%` }}
                role="progressbar"
                aria-valuenow={haveCount}
                aria-valuemin={0}
                aria-valuemax={totalMarkers}
                aria-label={`${haveCount} из ${totalMarkers}`}
              />
            </div>

            <button
              type="button"
              onClick={() => setMissingOpen((v) => !v)}
              aria-expanded={missingOpen}
              className="mt-3 flex w-full items-center justify-between gap-2 text-hl-secondary text-muted-foreground"
            >
              <span>Показать, чего не хватает ({data.missing.length})</span>
              {missingOpen ? (
                <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              )}
            </button>
            {missingOpen && (
              <ul className="mt-2 space-y-1 border-t border-border pt-2">
                {data.missing.map((m) => (
                  <li key={m.code} className="text-hl-secondary text-muted-foreground">
                    {capitalizeFirst(m.canonical_name_ru)}
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={() => setLocation('/upload')}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-surface bg-primary py-2.5 text-sm font-semibold text-primary-foreground"
            >
              <UploadCloud className="h-4 w-4" aria-hidden="true" />
              Загрузить анализ
            </button>
          </div>
        )}
      </main>
    </MobileFrame>
  );
}
